import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PasswordIcon from "@mui/icons-material/Password";
import LoginIcon from "@mui/icons-material/Login";
import BusinessIcon from "@mui/icons-material/Business";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import PeopleIcon from "@mui/icons-material/People";
import SettingsIcon from "@mui/icons-material/Settings";
import React, { useContext, useMemo } from "react";
import { useTextDomainContext } from "@/app/providers";

interface MenuItemPartnerProps<TRow extends object = Record<string, unknown>> {
  row?: TRow;
  menuFor?: string;
  handleClose: () => void;
  menuClick: (_action: string, _row?: TRow) => void;
  showItems?: Record<string, boolean | undefined>;
}

export const MenuItemPartner = <TRow extends object = Record<string, unknown>>(
  props: MenuItemPartnerProps<TRow>,
): React.ReactElement => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  //#region useMemo

  const menuFor = useMemo(() => {
    return props.menuFor ?? "";
  }, [props.menuFor]);

  //#endregion useMemo

  //#region Handlers

  const handleClose = () => {
    props.handleClose();
  };

  const handleEditAction = () => {
    props.menuClick(`${menuFor}-edit`, props.row);
    handleClose();
  };

  const handleTenantsAction = () => {
    props.menuClick(`${menuFor}-tenants`, props.row);
    handleClose();
  };

  const handleUsersAction = () => {
    props.menuClick(`${menuFor}-users`, props.row);
    handleClose();
  };

  const handleSettingsAction = () => {
    props.menuClick(`${menuFor}-settings`, props.row);
    handleClose();
  };

  const handlePasswordAction = () => {
    props.menuClick(`${menuFor}-password`, props.row);
    handleClose();
  };

  const handleCompanyAction = () => {
    props.menuClick(`${menuFor}-company`, props.row);
    handleClose();
  };

  const handleLoginAction = () => {
    props.menuClick(`${menuFor}-login`, props.row);
    handleClose();
  };

  const handleDeleteAction = () => {
    props.menuClick(`${menuFor}-delete`, props.row);
    handleClose();
  };

  //#endregion Handlers

  return (
    <>
      {props?.showItems?.showEdit && (
        <MenuItem onClick={handleEditAction}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={gettext("Edit")} />
        </MenuItem>
      )}

      {props?.showItems?.showTenants && (
        <MenuItem onClick={handleTenantsAction}>
          <ListItemIcon>
            <BusinessCenterIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={gettext("Corporate Groups")} />
        </MenuItem>
      )}

      {props?.showItems?.showUsers && (
        <MenuItem onClick={handleUsersAction}>
          <ListItemIcon>
            <PeopleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={gettext("Users")} />
        </MenuItem>
      )}

      {props?.showItems?.showSettings && (
        <MenuItem onClick={handleSettingsAction}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={gettext("Settings")} />
        </MenuItem>
      )}

      {props?.showItems?.showChangePassword && (
        <MenuItem onClick={handlePasswordAction}>
          <ListItemIcon>
            <PasswordIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={gettext("Change Password")} />
        </MenuItem>
      )}

      {props?.showItems?.showCompanies && (
        <MenuItem onClick={handleCompanyAction}>
          <ListItemIcon>
            <BusinessIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={gettext("Companies")} />
        </MenuItem>
      )}

      {props?.showItems?.showLoginAs && (
        <MenuItem onClick={handleLoginAction}>
          <ListItemIcon>
            <LoginIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={gettext("Login as")} />
        </MenuItem>
      )}

      {props?.showItems?.showDeleteSingle && (
        <MenuItem disabled onClick={handleDeleteAction}>
          <ListItemIcon>
            <DeleteIcon color="error" fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={gettext("Delete")}
            slotProps={{
              primary: {
                color: "error",
              },
            }}
          />
        </MenuItem>
      )}
    </>
  );
};
